import {
  CATALOGO_VERSAO,
  SERVICOS_OFICIAIS,
  barbeirosOficiais,
} from './barbeariaCatalogo';
import type {
  BarbeariaAgendamento,
  BarbeariaBarbeiro,
  BarbeariaBloqueio,
  BarbeariaDespesa,
  BarbeariaEstado,
  BarbeariaProdutoEstoque,
  BarbeariaServico,
  MetodoPagamento,
  StatusAgendamento,
} from './barbeariaTipos';

export function uid(prefix = 'bb'): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function agoraISO(): string {
  return new Date().toISOString();
}

/** Converte "HH:mm" em minutos desde 00:00. */
export function horaParaMinutos(hora: string): number {
  const m = String(hora || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
}

export function minutosParaHora(total: number): string {
  const t = Math.max(0, Math.floor(total));
  const h = Math.floor(t / 60);
  const m = t % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function somarMinutosHora(hora: string, minutos: number): string {
  return minutosParaHora(horaParaMinutos(hora) + minutos);
}

export function estadoInicialBarbearia(): BarbeariaEstado {
  const agora = agoraISO();
  return {
    versao: 1,
    config: {
      nomeLoja: 'Carioca Barbershop',
      horarioAbertura: '06:00',
      /** 24:00 = meia-noite (último horário precisa terminar até 00:00). */
      horarioFechamento: '24:00',
      intervaloSlotMin: 30,
      lembreteMinutosAntes: 60,
      catalogoVersao: CATALOGO_VERSAO,
      linkPublicoAgendamento: '',
      whatsappLoja: '',
    },
    clientes: [],
    barbeiros: barbeirosOficiais(agora),
    servicos: SERVICOS_OFICIAIS.map((s) => ({ ...s })),
    agendamentos: [],
    remocoesAgendamento: {},
    bloqueios: [],
    despesas: [],
    estoque: [
      {
        id: uid('stk'),
        nome: 'Pomada modeladora',
        qtdAtual: 12,
        precoCusto: 18,
        precoVenda: 35,
        atualizadoEm: agora,
      },
      {
        id: uid('stk'),
        nome: 'Golas higiênicas (pct)',
        qtdAtual: 40,
        precoCusto: 8,
        precoVenda: 0,
        atualizadoEm: agora,
      },
    ],
    atualizadoEm: agora,
  };
}

/** Garante catálogo oficial (preços, imagens, CARIOCA/SUB). */
export function aplicarCatalogoOficial(estado: BarbeariaEstado): BarbeariaEstado {
  const agora = agoraISO();
  return {
    ...estado,
    barbeiros: barbeirosOficiais(agora).map((b) => {
      const prev = estado.barbeiros.find((x) => x.id === b.id || x.nome.toUpperCase() === b.nome);
      return prev ? { ...b, comissaoPct: prev.comissaoPct, criadoEm: prev.criadoEm } : b;
    }),
    servicos: SERVICOS_OFICIAIS.map((s) => ({ ...s })),
    config: {
      ...estado.config,
      catalogoVersao: CATALOGO_VERSAO,
      // Expediente oficial: 6h → meia-noite (força no app e no link)
      horarioAbertura: '06:00',
      horarioFechamento: '24:00',
    },
  };
}

export function normalizarEstadoBarbearia(raw: unknown): BarbeariaEstado {
  const base = estadoInicialBarbearia();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<BarbeariaEstado>;
  const merged: BarbeariaEstado = {
    versao: 1,
    config: { ...base.config, ...(r.config || {}) },
    clientes: Array.isArray(r.clientes) ? r.clientes : [],
    barbeiros: Array.isArray(r.barbeiros) && r.barbeiros.length > 0 ? r.barbeiros : base.barbeiros,
    servicos: Array.isArray(r.servicos) && r.servicos.length > 0 ? r.servicos : base.servicos,
    agendamentos: Array.isArray(r.agendamentos) ? r.agendamentos : [],
    remocoesAgendamento:
      r.remocoesAgendamento && typeof r.remocoesAgendamento === 'object'
        ? { ...(r.remocoesAgendamento as Record<string, string>) }
        : {},
    bloqueios: Array.isArray(r.bloqueios) ? r.bloqueios : [],
    despesas: Array.isArray(r.despesas) ? r.despesas : [],
    estoque: Array.isArray(r.estoque) ? r.estoque : base.estoque,
    atualizadoEm: String(r.atualizadoEm || agoraISO()),
  };
  return aplicarCatalogoOficial(merged);
}

/** App da loja (NW.js / Capacitor / file://) — tem Admin completo. */
export function isAppLojaNativo(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const w = window as Window & {
      nw?: unknown;
      Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string };
    };
    if (w.nw) return true;
    if (w.Capacitor?.isNativePlatform?.()) return true;
    const plat = w.Capacitor?.getPlatform?.();
    if (plat === 'android' || plat === 'ios') return true;
    if (window.location.protocol === 'file:') return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * Site público na internet = SEMPRE só agendamento.
 * Remover ?agendar=1 não abre o Admin.
 * NW.exe / APK / localhost de desenvolvimento = app completo da loja.
 */
export function isModoClientePublico(): boolean {
  if (typeof window === 'undefined') return false;
  if (isAppLojaNativo()) return false;
  try {
    const host = window.location.hostname || '';
    // Prévia local no navegador (vite) = loja completa
    if (host === 'localhost' || host === '127.0.0.1') return false;
    // Qualquer site publicado (ex.: pages.dev) = só cliente
    return true;
  } catch {
    return true;
  }
}

/** Dois intervalos [aIni,aFim) e [bIni,bFim) se cruzam. */
function intervalosConflitam(aIni: number, aFim: number, bIni: number, bFim: number): boolean {
  return aIni < bFim && bIni < aFim;
}

/**
 * Verifica conflito de agenda do barbeiro:
 * - ignora cancelados
 * - considera duração do serviço
 * - respeita bloqueios do barbeiro ou globais
 */
export function verificarConflitoAgenda(params: {
  estado: BarbeariaEstado;
  barbeiroId: string;
  dataISO: string;
  horaInicio: string;
  duracaoMin: number;
  ignorarAgendamentoId?: string;
}): { ok: true } | { ok: false; motivo: string } {
  const { estado, barbeiroId, dataISO, horaInicio, duracaoMin, ignorarAgendamentoId } = params;
  const ini = horaParaMinutos(horaInicio);
  const fim = ini + Math.max(1, duracaoMin);

  const aberto = horaParaMinutos(estado.config.horarioAbertura);
  const fecha = horaParaMinutos(estado.config.horarioFechamento);
  if (ini < aberto || fim > fecha) {
    return {
      ok: false,
      motivo: `Fora do expediente (${estado.config.horarioAbertura}–${estado.config.horarioFechamento}).`,
    };
  }

  for (const b of estado.bloqueios) {
    if (b.dataISO !== dataISO) continue;
    if (b.barbeiroId && b.barbeiroId !== barbeiroId) continue;
    const bIni = horaParaMinutos(b.horaInicio);
    const bFim = horaParaMinutos(b.horaFim);
    if (intervalosConflitam(ini, fim, bIni, bFim)) {
      return { ok: false, motivo: `Horário bloqueado${b.motivo ? `: ${b.motivo}` : ''}.` };
    }
  }

  for (const a of estado.agendamentos) {
    if (a.id === ignorarAgendamentoId) continue;
    if (a.barbeiroId !== barbeiroId || a.dataISO !== dataISO) continue;
    if (a.status === 'cancelado') continue;
    const serv = estado.servicos.find((s) => s.id === a.servicoId);
    const dur = serv?.duracaoMin ?? 30;
    const aIni = horaParaMinutos(a.horaInicio);
    const aFim = aIni + dur;
    if (intervalosConflitam(ini, fim, aIni, aFim)) {
      return { ok: false, motivo: 'Já existe agendamento neste horário para o barbeiro.' };
    }
  }

  return { ok: true };
}

/** Slots livres do dia para um barbeiro + duração de serviço. */
export function listarSlotsDisponiveis(
  estado: BarbeariaEstado,
  dataISO: string,
  barbeiroId: string,
  duracaoMin: number
): string[] {
  const step = Math.max(15, estado.config.intervaloSlotMin || 30);
  const aberto = horaParaMinutos(estado.config.horarioAbertura);
  const fecha = horaParaMinutos(estado.config.horarioFechamento);
  const slots: string[] = [];
  for (let t = aberto; t + duracaoMin <= fecha; t += step) {
    const hora = minutosParaHora(t);
    const check = verificarConflitoAgenda({
      estado,
      barbeiroId,
      dataISO,
      horaInicio: hora,
      duracaoMin,
    });
    if (check.ok) slots.push(hora);
  }
  return slots;
}

export function valorDoAgendamento(
  a: BarbeariaAgendamento,
  servicos: BarbeariaServico[]
): number {
  if (typeof a.valorCobrado === 'number' && Number.isFinite(a.valorCobrado)) {
    return Math.max(0, a.valorCobrado);
  }
  const s = servicos.find((x) => x.id === a.servicoId);
  return Math.max(0, Number(s?.preco) || 0);
}

export interface ResumoFinanceiroPeriodo {
  bruto: number;
  comissoes: number;
  casa: number;
  qtdAtendimentos: number;
  porMetodo: Record<MetodoPagamento, number>;
  porBarbeiro: Array<{ barbeiroId: string; nome: string; bruto: number; comissao: number }>;
}

/**
 * Faturamento bruto = soma dos agendamentos concluídos no período.
 * Comissão = bruto × (comissaoPct do barbeiro / 100).
 * Casa = bruto − comissão.
 */
export function calcularFaturamento(
  estado: BarbeariaEstado,
  dataIniISO: string,
  dataFimISO: string
): ResumoFinanceiroPeriodo {
  const porMetodo: Record<MetodoPagamento, number> = {
    pix: 0,
    dinheiro: 0,
    credito: 0,
    debito: 0,
  };
  const mapaBrb = new Map<string, { nome: string; bruto: number; comissao: number }>();

  let bruto = 0;
  let comissoes = 0;
  let qtd = 0;

  for (const a of estado.agendamentos) {
    if (a.status !== 'concluido') continue;
    if (a.dataISO < dataIniISO || a.dataISO > dataFimISO) continue;
    const valor = valorDoAgendamento(a, estado.servicos);
    const brb = estado.barbeiros.find((b) => b.id === a.barbeiroId);
    const pct = Math.min(100, Math.max(0, Number(brb?.comissaoPct) || 0));
    // Comissão automática configurável por barbeiro
    const comissao = (valor * pct) / 100;
    bruto += valor;
    comissoes += comissao;
    qtd += 1;
    const metodo = (a.metodoPagamento || 'pix') as MetodoPagamento;
    if (metodo in porMetodo) porMetodo[metodo] += valor;

    const key = a.barbeiroId;
    const prev = mapaBrb.get(key) || {
      nome: brb?.nome || 'Barbeiro',
      bruto: 0,
      comissao: 0,
    };
    prev.bruto += valor;
    prev.comissao += comissao;
    mapaBrb.set(key, prev);
  }

  return {
    bruto,
    comissoes,
    casa: bruto - comissoes,
    qtdAtendimentos: qtd,
    porMetodo,
    porBarbeiro: [...mapaBrb.entries()].map(([barbeiroId, v]) => ({
      barbeiroId,
      nome: v.nome,
      bruto: v.bruto,
      comissao: v.comissao,
    })),
  };
}

export function somarDespesas(
  despesas: BarbeariaDespesa[],
  dataIniISO: string,
  dataFimISO: string
): { fixo: number; variavel: number; total: number } {
  let fixo = 0;
  let variavel = 0;
  for (const d of despesas) {
    if (d.dataISO < dataIniISO || d.dataISO > dataFimISO) continue;
    if (d.tipo === 'fixo') fixo += Number(d.valor) || 0;
    else variavel += Number(d.valor) || 0;
  }
  return { fixo, variavel, total: fixo + variavel };
}

export function margemProduto(p: BarbeariaProdutoEstoque): number {
  const custo = Number(p.precoCusto) || 0;
  const venda = Number(p.precoVenda) || 0;
  if (venda <= 0) return 0;
  return ((venda - custo) / venda) * 100;
}

/** Monta data/hora ISO local aproximada do lembrete (início − X minutos). */
export function calcularMomentoLembrete(
  dataISO: string,
  horaInicio: string,
  minutosAntes: number
): string {
  const [y, mo, d] = dataISO.split('-').map(Number);
  const [hh, mm] = horaInicio.split(':').map(Number);
  const dt = new Date(y, (mo || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
  dt.setMinutes(dt.getMinutes() - Math.max(0, minutosAntes));
  return dt.toISOString();
}

export function linkWhatsAppLembrete(
  telefone: string,
  nomeCliente: string,
  dataISO: string,
  hora: string,
  servicoNome: string,
  loja: string
): string {
  const digitos = String(telefone || '').replace(/\D/g, '');
  const comPais = digitos.length <= 11 ? `55${digitos}` : digitos;
  const dataBr = dataISO.split('-').reverse().join('/');
  const msg = encodeURIComponent(
    `Olá ${nomeCliente}! Lembrete da ${loja}: seu horário de ${servicoNome} está marcado para ${dataBr} às ${hora}. Por favor, chegue 10 minutos antes. Até logo!`
  );
  return `https://wa.me/${comPais}?text=${msg}`;
}

/** Agenda os dois lembretes padrão: 1 dia e 1 hora antes. */
export function montarLembretesAgendamento(
  dataISO: string,
  horaInicio: string
): {
  lembreteWhatsApp: true;
  lembrete1DiaPara: string;
  lembrete1HoraPara: string;
  lembreteAgendadoPara: string;
} {
  const umDia = calcularMomentoLembrete(dataISO, horaInicio, 24 * 60);
  const umaHora = calcularMomentoLembrete(dataISO, horaInicio, 60);
  return {
    lembreteWhatsApp: true,
    lembrete1DiaPara: umDia,
    lembrete1HoraPara: umaHora,
    lembreteAgendadoPara: umaHora,
  };
}

export function fmtBrl(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function statusLabel(s: StatusAgendamento): string {
  switch (s) {
    case 'confirmado':
      return 'Confirmado';
    case 'em_andamento':
      return 'Em andamento';
    case 'concluido':
      return 'Concluído';
    case 'cancelado':
      return 'Cancelado';
    default:
      return s;
  }
}

export function metodoLabel(m: MetodoPagamento): string {
  switch (m) {
    case 'pix':
      return 'Pix';
    case 'dinheiro':
      return 'Dinheiro';
    case 'credito':
      return 'Cartão crédito';
    case 'debito':
      return 'Cartão débito';
    default:
      return m;
  }
}

export function inicioFimSemanaISO(dataRef = new Date()): { ini: string; fim: string } {
  const d = new Date(dataRef);
  const dia = d.getDay();
  const diffSeg = dia === 0 ? -6 : 1 - dia;
  const seg = new Date(d);
  seg.setDate(d.getDate() + diffSeg);
  const dom = new Date(seg);
  dom.setDate(seg.getDate() + 6);
  const iso = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { ini: iso(seg), fim: iso(dom) };
}

export function inicioFimMesISO(dataRef = new Date()): { ini: string; fim: string } {
  const y = dataRef.getFullYear();
  const m = dataRef.getMonth();
  const ini = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const ultimo = new Date(y, m + 1, 0).getDate();
  const fim = `${y}-${String(m + 1).padStart(2, '0')}-${String(ultimo).padStart(2, '0')}`;
  return { ini, fim };
}

export function hojeISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function listarDatasCalendario(ano: number, mes0: number): Array<string | null> {
  const primeiro = new Date(ano, mes0, 1);
  const diasNoMes = new Date(ano, mes0 + 1, 0).getDate();
  const inicioSemana = (primeiro.getDay() + 6) % 7; // segunda = 0
  const cells: Array<string | null> = [];
  for (let i = 0; i < inicioSemana; i++) cells.push(null);
  for (let dia = 1; dia <= diasNoMes; dia++) {
    cells.push(
      `${ano}-${String(mes0 + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
    );
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export type { BarbeariaBarbeiro, BarbeariaBloqueio };
